import { useState, useEffect, memo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronRight, Package, ExternalLink, AlertTriangle, AlertCircle, EyeOff, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProductionStatusBadge } from './ProductionStatusBadge';
import type { QueuedOrder } from '../types';
import { isStockBlocked } from '../utils/order-eligibility';

interface OrderCardProps {
  order: QueuedOrder;
  onSelect?: (orderId: string, selected: boolean) => void;
  isSelected?: boolean;
  showCheckbox?: boolean;
  /** Show production status badge (for All Orders tab only) */
  showProductionStatus?: boolean;
}

/**
 * Format remaining hold time as a human-readable countdown.
 * Returns null if the hold has expired.
 */
function formatHoldRemaining(heldUntil: string): string | null {
  const remaining = new Date(heldUntil).getTime() - Date.now();
  if (remaining <= 0) return null;

  const minutes = Math.ceil(remaining / 60_000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

/**
 * OrderCard - Collapsible card for displaying order information.
 *
 * UX DECISION: No eligibility badges on Ready/OOS tabs (redundant).
 * - Ready/OOS tabs: Just show order info (tab context tells you the status)
 * - All Orders tab: Show production status badge + warning icon for problems
 *
 * Uses React.memo for performance (B2B App pattern).
 * Interactive hover states from B2B design system.
 */
export const OrderCard = memo(function OrderCard({
  order,
  onSelect,
  isSelected,
  showCheckbox,
  showProductionStatus = false
}: OrderCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Compute hold state
  const isHeld = !!(order.held_until && new Date(order.held_until) > new Date());
  const [holdText, setHoldText] = useState<string | null>(
    isHeld ? formatHoldRemaining(order.held_until!) : null
  );

  // Update countdown every 30 seconds while held
  useEffect(() => {
    if (!isHeld || !order.held_until) return;
    const update = () => setHoldText(formatHoldRemaining(order.held_until!));
    update();
    const interval = setInterval(update, 30_000);
    return () => clearInterval(interval);
  }, [isHeld, order.held_until]);

  // Build Shopify admin URL - shopify_order_id is numeric
  const shopifyAdminUrl = `https://admin.shopify.com/store/threadheads/orders/${order.shopify_order_id}`;
  const totalItems = order.line_items.reduce((sum, item) => sum + item.quantity, 0);

  // Determine if order has problems (for warning icon on All Orders tab)
  const hasProblems = isStockBlocked(order) ||
                      order.eligibility_status === 'HELD';
  const isExcluded = order.eligibility_status === 'EXCLUDED';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={cn(
        "border rounded-lg overflow-hidden bg-card",
        "transition-all duration-200",
        "hover:border-primary/20 hover:shadow-sm",
        isOpen && "shadow-sm",
        isSelected && "border-primary ring-2 ring-primary/20"
      )}>
        <CollapsibleTrigger asChild>
          {/* Mobile responsive header - stacks on small screens */}
          <button className={cn(
            "flex flex-col sm:flex-row sm:items-center sm:justify-between w-full",
            "px-4 py-3 text-left gap-2 sm:gap-0",
            "transition-colors duration-150",
            "hover:bg-muted/40"
          )}>
            <div className="flex items-center gap-3">
              {showCheckbox && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect?.(order.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20"
                />
              )}
              <ChevronRight className={cn(
                "h-4 w-4 text-muted-foreground shrink-0",
                "transition-transform duration-200",
                isOpen && "rotate-90"
              )} />
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-foreground">
                    {order.order_number ?? `#${String(order.shopify_order_id).slice(-6)}`}
                  </span>
                  <Badge variant="outline" size="sm" className="gap-1">
                    <Package className="h-3 w-3" />
                    {totalItems} {totalItems === 1 ? 'item' : 'items'}
                  </Badge>
                  {/* Pending badge for held orders (2-hour editing window) */}
                  {isHeld && holdText && (
                    <Badge variant="secondary" size="sm" className="gap-1 text-amber-700 bg-amber-50 border-amber-200">
                      <Clock className="h-3 w-3" />
                      Pending {holdText}
                    </Badge>
                  )}
                  {/* Priority badge for re-queued orders (BLU-259) */}
                  {order.is_priority && (
                    <Badge variant="outline" size="sm" className="gap-1 border-amber-400 text-amber-700 dark:text-amber-400">
                      Priority
                    </Badge>
                  )}
                  {/* Warning icon for problems (All Orders tab only) */}
                  {showProductionStatus && hasProblems && !isHeld && (
                    <AlertCircle
                      className="h-4 w-4 text-red-500"
                      aria-label={isStockBlocked(order) ? 'Out of stock' : 'On hold'}
                    />
                  )}
                </div>
                {order.customer_name && (
                  <span className="text-sm text-muted-foreground">
                    {order.customer_name}
                  </span>
                )}
              </div>
            </div>

            {/* Right side: Production status (All Orders) or just timestamp */}
            <div className="flex items-center gap-3 sm:ml-auto pl-7 sm:pl-0">
              {showProductionStatus && (
                isExcluded ? (
                  <Badge variant="muted" className="gap-1.5">
                    <EyeOff className="h-3 w-3" />
                    Excluded
                  </Badge>
                ) : (
                  <ProductionStatusBadge status={order.prod_order?.production_status ?? null} />
                )
              )}
              <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                {formatDistanceToNow(new Date(order.order_date), { addSuffix: true })}
              </span>
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 py-4 border-t bg-muted/20 space-y-4">
            {/* Line Items */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                Line Items
              </h4>
              <div className="space-y-1.5">
                {order.line_items.map(item => (
                  <div key={item.id} className="line-item">
                    <span className="line-item-name truncate-flex">
                      {item.product_name}
                      {item.variant_title && (
                        <span className="text-foreground/70"> · {item.variant_title}</span>
                      )}
                    </span>
                    <span className="line-item-qty">×{item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Blocked Items (for OOS orders) */}
            {order.blocked_items && order.blocked_items.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <h4 className="text-xs font-medium uppercase tracking-wide text-red-700 flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Stock Issues
                </h4>
                <div className="space-y-1.5">
                  {order.blocked_items.map(item => (
                    <div key={item.sku} className="text-sm flex items-baseline gap-2">
                      <code className="text-xs font-mono bg-red-100 px-1.5 py-0.5 rounded text-red-800">
                        {item.sku}
                      </code>
                      <span className="text-red-700 tabular-nums">
                        Need {item.needed}, have {item.available}
                        {item.deficit > 0 && (
                          <span className="font-medium"> (−{item.deficit})</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {order.tags && order.tags.length > 0 && (
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                  Tags
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {order.tags.map(tag => (
                    <Badge key={tag} variant="muted" size="sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-border/50">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                asChild
              >
                <a
                  href={shopifyAdminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View order in Shopify (opens in new tab)"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View in Shopify
                </a>
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
});
